import React, { useState, useEffect, useRef } from 'react';
import { Icons as I } from '../../components/pack-icons';
import i18n, { markExplicitLanguageChoice } from '../../i18n';

const STORAGE_KEY = 'dathent-lang';

// Interface-language picker for the onboarding chat's pristine (empty-history)
// state. Visually identical to the AppShell LangSwitch — same CSS classes
// (.lang-dd/.lang-trigger/.lang-panel/.lang-option, global via shell.css) and
// the same 10 supported languages, so it reads as one consistent control
// across the whole app.
const LANGS = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'uk', name: 'Українська', label: 'UA' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es', name: 'Español' },
  { code: 'pl', name: 'Polski' },
  { code: 'cs', name: 'Čeština' },
  { code: 'lt', name: 'Lietuvių' },
  { code: 'lv', name: 'Latviešu' },
  { code: 'sv', name: 'Svenska' },
];

const badge = (l) => (l.label || l.code).toUpperCase();

/**
 * OnboardingLangPicker — dropdown for the onboarding chat's interface
 * language. Wires to i18next exactly like LangSwitch (same storage key +
 * explicit-choice lock), just rendered inline in the onboarding topbar.
 */
export default function OnboardingLangPicker() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    return localStorage.getItem(STORAGE_KEY) || i18n?.language || 'en';
  });
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handlePick = (code) => {
    setLang(code);
    localStorage.setItem(STORAGE_KEY, code);
    // Lock this in as a deliberate pick so brand-language auto-sync won't undo it.
    markExplicitLanguageChoice();
    setOpen(false);
    i18n?.changeLanguage?.(code);
  };

  const active = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className={`lang-dd ${open ? 'is-open' : ''}`} ref={ref}>
      <button className="lang-trigger" onClick={() => setOpen((o) => !o)} type="button">
        <span className="code">{badge(active)}</span>
        <span className="lang-name">{active.name}</span>
        <I.chevDown className="caret" size={11} />
      </button>
      <div className="lang-panel" role="menu">
        {LANGS.map((l) => (
          <button
            key={l.code}
            className={`lang-option ${lang === l.code ? 'is-active' : ''}`}
            onClick={() => handlePick(l.code)}
            type="button"
          >
            <span className="lc">{badge(l)}</span>
            <span className="ln">{l.name}</span>
            {lang === l.code && <I.check className="lk" size={14} />}
          </button>
        ))}
      </div>
    </div>
  );
}
